import React from 'react';
import { cn } from '../../utils/cn';

interface TypographyProps {
  className?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}

interface HeadingProps extends TypographyProps {
  level?: 1 | 2 | 3 | 4;
  as?: 'h1' | 'h2' | 'h3' | 'h4';
}

/**
 * Heading Component - Standardized headings
 */
export function Heading({ 
  level = 1, 
  as, 
  className, 
  children,
  style 
}: HeadingProps) {
  const Component = as || (`h${level}` as 'h1' | 'h2' | 'h3' | 'h4');
  
  const levelStyles = {
    1: 'text-4xl font-bold leading-tight',
    2: 'text-3xl font-semibold leading-tight',
    3: 'text-2xl font-semibold leading-normal',
    4: 'text-xl font-medium leading-normal',
  };
  
  return (
    <Component 
      className={cn(
        'text-primary',
        levelStyles[level],
        className
      )}
      style={style}
    >
      {children}
    </Component>
  );
}

/**
 * Text Component - Standardized text
 */
export function Text({ 
  size = 'base',
  weight = 'normal',
  className,
  children,
  style
}: TypographyProps & {
  size?: 'xs' | 'sm' | 'base' | 'lg';
  weight?: 'light' | 'normal' | 'medium' | 'semibold';
}) {
  const sizeStyles = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
  };
  
  const weightStyles = {
    light: 'font-normal',
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
  };
  
  return (
    <p 
      className={cn(
        'text-primary',
        sizeStyles[size],
        weightStyles[weight],
        className
      )}
      style={style}
    >
      {children}
    </p>
  );
}

/**
 * Caption Component - Small text for captions, labels, etc.
 */
export function Caption({ className, children, style }: TypographyProps) {
  return (
    <span 
      className={cn(
        'text-xs text-muted',
        className
      )}
      style={style}
    >
      {children}
    </span>
  );
}

/**
 * Label Component - Form labels
 */
export function Label({ className, children, style }: TypographyProps) {
  return (
    <label 
      className={cn(
        'label',
        className
      )}
      style={style}
    >
      {children}
    </label>
  );
}

/**
 * Code Component - Inline code snippets
 */
export function Code({ className, children, style }: TypographyProps) {
  return (
    <code 
      className={cn(
        'px-1 py-0.5 bg-gray-100 dark:bg-gray-800 text-sm font-mono rounded',
        'text-accent-600 dark:text-accent-400',
        className
      )}
      style={style}
    >
      {children}
    </code>
  );
}

/**
 * Pre Component - Code blocks
 */
export function Pre({ className, children, style }: TypographyProps) {
  return (
    <pre 
      className={cn(
        'p-4 bg-gray-900 dark:bg-gray-950 text-gray-100 rounded-lg overflow-x-auto',
        'font-mono text-sm leading-relaxed',
        className
      )}
      style={style}
    >
      {children}
    </pre>
  );
}

/**
 * Strong Component - Bold text
 */
export function Strong({ className, children, style }: TypographyProps) {
  return (
    <strong 
      className={cn(
        'font-semibold text-primary',
        className
      )}
      style={style}
    >
      {children}
    </strong>
  );
}

/**
 * Em Component - Emphasized text
 */
export function Em({ className, children, style }: TypographyProps) {
  return (
    <em 
      className={cn(
        'italic text-secondary',
        className
      )}
      style={style}
    >
      {children}
    </em>
  );
}

/**
 * Link Component - Styled links
 */
export function Link({ 
  href,
  className, 
  children,
  style,
  ...props
}: TypographyProps & React.AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a 
      href={href}
      className={cn(
        'text-link hover:underline transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500',
        className
      )}
      style={style}
      {...props}
    >
      {children}
    </a>
  );
}